import React, { FC, useCallback, useEffect, useState } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import { GetRechnungDetailsDto, PaginatedGetRechnungDto } from "../../../../../api-client";
import { SearchTransaction } from "../../../Insurance/Base/types/insuranceBills.types";
import { useDebounce } from "../../../Insurance/Base/hooks/useDebounce";
import { InsuranceBillsPagination } from "../../../Insurance/Base/components/InsuranceBillsPagination";
import useStore from "../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../stores/Transactions/Transactions.provider";
import TransactionsOverviewTable from "./TransactionsOverviewTable";
import { recnungTransactionMethods } from "../types/kasse.types";
import { Dayjs } from "dayjs";

interface RechnungTabsContainerProps {
  accountId: string;
  searchValue: string | null;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
}

enum RechnungTab {
  UNPAID = 0,
  PAID = 1,
  MAHNUNG = 2,
}

const TAB_LABELS = ["Offene", "Bezahlt", "Mahnung"];

/**
 * Renders three independent tabs for Rechnung data:
 * - Offene: All unpaid Rechnungen (NOT_PAID / PARTIAL)
 * - Bezahlt: All fully paid Rechnungen
 * - Mahnung: Rechnungen eligible for a new bill reminder
 *
 * Each tab manages its own pagination state and fetches data independently.
 */
const RechnungTabsContainer: FC<RechnungTabsContainerProps> = ({
  accountId,
  searchValue,
  startDate,
  endDate,
}) => {
  const transactionStore = useStore(TransactionsContext);
  const { getUnpaidRechnungen, getPaidRechnungen, getBillReminderEligibleRechnungen } =
    transactionStore;

  const [activeTab, setActiveTab] = useState<RechnungTab>(RechnungTab.UNPAID);

  // Per-tab pagination state
  const [pages, setPages] = useState({ 0: 1, 1: 1, 2: 1 });
  const [limits, setLimits] = useState({ 0: 15, 1: 15, 2: 15 });

  const [paginatedData, setPaginatedData] = useState<PaginatedGetRechnungDto | null>(null);
  const [transactions, setTransactions] = useState<GetRechnungDetailsDto[]>([]);

  const debouncedSearch = useDebounce(searchValue, 400);

  const page = pages[activeTab];
  const limit = limits[activeTab];

  const setPage = (newPage: number) => {
    setPages((prev) => ({ ...prev, [activeTab]: newPage }));
  };

  const setLimit = (newLimit: number) => {
    setLimits((prev) => ({ ...prev, [activeTab]: newLimit }));
    setPages((prev) => ({ ...prev, [activeTab]: 1 }));
  };

  const fetchData = useCallback(async () => {
    const query: SearchTransaction = {
      page,
      limit,
      startDate: startDate?.format("YYYY-MM-DD"),
      endDate: endDate?.format("YYYY-MM-DD"),
      searchStr: debouncedSearch || undefined,
    };

    let result: PaginatedGetRechnungDto;

    switch (activeTab) {
      case RechnungTab.UNPAID:
        result = await getUnpaidRechnungen(accountId, query);
        break;
      case RechnungTab.PAID:
        result = await getPaidRechnungen(accountId, query);
        break;
      case RechnungTab.MAHNUNG:
        result = await getBillReminderEligibleRechnungen(accountId, query);
        break;
    }

    setPaginatedData(result);
    setTransactions(result.data);
  }, [
    activeTab,
    accountId,
    page,
    limit,
    startDate,
    endDate,
    debouncedSearch,
    getUnpaidRechnungen,
    getPaidRechnungen,
    getBillReminderEligibleRechnungen,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTabChange = (_: React.SyntheticEvent, newTab: RechnungTab) => {
    setActiveTab(newTab);
  };

  return (
    <Box>
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 1 }}>
        {TAB_LABELS.map((label, idx) => (
          <Tab key={idx} label={label} />
        ))}
      </Tabs>

      {transactions.length > 0 ? (
        <TransactionsOverviewTable
          transactions={transactions}
          transactionMethod={recnungTransactionMethods.RECHNUNG}
          onRefresh={fetchData}
        />
      ) : (
        <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
          Für die ausgewählten Filter wurden keine Rechnungen gefunden.
        </Box>
      )}

      {paginatedData && paginatedData.meta.totalItems > 0 && (
        <InsuranceBillsPagination
          totalItems={paginatedData.meta.totalItems}
          totalPages={paginatedData.meta.totalPages}
          limit={limit}
          page={page}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}
    </Box>
  );
};

export default RechnungTabsContainer;
